<template>
  <div class="shop-group-multi-select" :class="rootClass" :style="wrapStyle">
    <el-select
      v-model="innerGroupId"
      :placeholder="groupPlaceholder"
      :size="size"
      clearable
      filterable
      :disabled="disabled || !shops.length"
      :style="groupStyle"
      class="shop-group-multi-select__group"
      @change="onGroupChange"
    >
      <el-option
        v-for="g in shopGroups"
        :key="'sg-' + g.id"
        :label="g.name"
        :value="Number(g.id)"
      />
    </el-select>
    <el-select
      :value="value"
      multiple
      :collapse-tags="collapseTags"
      filterable
      :clearable="clearable"
      :placeholder="placeholder"
      :size="size"
      :disabled="disabled"
      :style="shopStyle"
      :class="shopSelectClass"
      class="shop-group-multi-select__shop"
      @input="onShopInput"
      @change="onShopChange"
    >
      <el-option
        v-for="shop in shops"
        :key="shop.id"
        :label="shopOptionLabel(shop)"
        :value="shop.id"
        :disabled="isShopDisabled(shop)"
      >
        <span v-if="showSourceTag && shop.source_type == 1">【抖店】</span>{{ shop.shop_name }}
        <span v-if="isAuthDisabled(shop)" class="shop-group-multi-select__unauth">（未授权）</span>
      </el-option>
    </el-select>
    <slot />
  </div>
</template>

<script>
import {
  isDouDianShopAuthorized,
  formatDouDianShopOptionLabel,
  isDouDianShopAuthDisabled,
  DOUDIAN_AUTH_REQUIRED_MSG,
} from "@/script/doudianLocalLogin";

/**
 * 多店铺选择 + 按分组一键勾选该组店铺（并入已选，不清空原选择）
 */
export default {
  name: "ShopGroupMultiSelect",
  props: {
    value: { type: Array, default: () => [] },
    shops: { type: Array, default: () => [] },
    disabled: { type: Boolean, default: false },
    placeholder: { type: String, default: "选择店铺（可多选）" },
    groupPlaceholder: { type: String, default: "按分组选店" },
    size: { type: String, default: "small" },
    collapseTags: { type: Boolean, default: true },
    clearable: { type: Boolean, default: true },
    showSourceTag: { type: Boolean, default: true },
    groupWidth: { type: [String, Number], default: "140px" },
    shopWidth: { type: [String, Number], default: "380px" },
    wrapStyle: { type: [String, Object], default: "" },
    shopSelectClass: { type: [String, Object, Array], default: "" },
    emptyGroupMessage: {
      type: String,
      default: "该分组下暂无可用店铺",
    },
    autoLoadGroups: { type: Boolean, default: true },
    /** (shop) => boolean，为 true 时禁用该店铺选项 */
    optionDisabled: { type: Function, default: null },
    /** 是否要求订购授权：未授权店置灰并标「未授权」 */
    requireAuthorized: { type: Boolean, default: false },
    /** 是否允许换行；工具栏场景建议 false，避免分组/选店竖着掉下来 */
    allowWrap: { type: Boolean, default: false },
  },
  data() {
    return {
      shopGroups: [],
      innerGroupId: null,
      _strippingAuth: false,
    };
  },
  computed: {
    rootClass() {
      return {
        "shop-group-multi-select--nowrap": !this.allowWrap,
      };
    },
    groupStyle() {
      const w = this.groupWidth;
      return { width: typeof w === "number" ? w + "px" : w };
    },
    shopStyle() {
      const w = this.shopWidth;
      return { width: typeof w === "number" ? w + "px" : w };
    },
  },
  watch: {
    shops: {
      handler() {
        this.stripUnauthorizedSelection();
      },
      deep: true,
    },
    value: {
      handler() {
        this.stripUnauthorizedSelection();
      },
    },
    requireAuthorized() {
      this.stripUnauthorizedSelection();
    },
  },
  mounted() {
    if (this.autoLoadGroups) {
      this.loadShopGroups();
    }
    this.stripUnauthorizedSelection();
  },
  methods: {
    isAuthDisabled(shop) {
      return this.requireAuthorized && isDouDianShopAuthDisabled(shop);
    },
    isShopDisabled(shop) {
      if (this.optionDisabled && this.optionDisabled(shop)) return true;
      return this.isAuthDisabled(shop);
    },
    shopOptionLabel(shop) {
      if (this.requireAuthorized) {
        return formatDouDianShopOptionLabel(shop);
      }
      return (shop && shop.shop_name) || "";
    },
    onShopInput(val) {
      this.$emit("input", val);
    },
    onShopChange(val) {
      if (this.requireAuthorized) {
        const next = this.filterAuthorizedIds(val);
        if (next.length !== (val || []).length) {
          this.$message.warning(DOUDIAN_AUTH_REQUIRED_MSG);
          this.$emit("input", next);
          this.$emit("change", next);
          return;
        }
      }
      this.$emit("change", val);
    },
    filterAuthorizedIds(ids) {
      const list = ids || [];
      return list.filter((id) => {
        const shop = (this.shops || []).find(
          (s) => s && (s.id === id || String(s.id) === String(id))
        );
        if (!shop) return true;
        return isDouDianShopAuthorized(shop);
      });
    },
    stripUnauthorizedSelection() {
      if (!this.requireAuthorized || this._strippingAuth) return;
      const cur = this.value || [];
      if (!cur.length) return;
      const next = this.filterAuthorizedIds(cur);
      if (next.length === cur.length) return;
      this._strippingAuth = true;
      this.$emit("input", next);
      this.$emit("change", next);
      this.$nextTick(() => {
        this._strippingAuth = false;
      });
    },
    async loadShopGroups() {
      try {
        const rs = await this.$api.post(
          "/admintooltaobaoaccount/getdoudianshopgroups",
          {}
        );
        if (rs.code == 1) {
          const data = rs.info || rs.data || {};
          this.shopGroups = data.list || [];
        }
      } catch (e) {
        this.shopGroups = [];
      }
    },
    /** 供父组件刷新分组列表 */
    refreshGroups() {
      return this.loadShopGroups();
    },
    clearGroup() {
      this.innerGroupId = null;
    },
    onGroupChange(gid) {
      if (gid == null || gid === "") {
        return;
      }
      const groupId = Number(gid);
      const groupShops = (this.shops || []).filter(
        (s) => Number(s.group_id) === groupId && !this.isShopDisabled(s)
      );
      if (!groupShops.length) {
        const hasUnauthorized = (this.shops || []).some(
          (s) => Number(s.group_id) === groupId && this.isAuthDisabled(s)
        );
        this.$message.warning(
          hasUnauthorized
            ? "该分组下暂无已授权店铺，请先订购并授权"
            : this.emptyGroupMessage
        );
        return;
      }
      const idSet = new Set((this.value || []).map((id) => String(id)));
      groupShops.forEach((s) => idSet.add(String(s.id)));
      const sampleId = this.shops[0] && this.shops[0].id;
      const useString = typeof sampleId === "string";
      const next = [...idSet].map((id) => (useString ? id : Number(id) || id));
      this.$emit("input", next);
      this.$emit("change", next);
      this.$message.success(`已勾选分组内 ${groupShops.length} 个店铺`);
    },
  },
};
</script>

<style scoped>
.shop-group-multi-select {
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  vertical-align: middle;
  flex-shrink: 0;
}
.shop-group-multi-select--nowrap {
  flex-wrap: nowrap;
  white-space: nowrap;
}
.shop-group-multi-select__group,
.shop-group-multi-select__shop {
  flex-shrink: 0;
}
.shop-group-multi-select__unauth {
  color: #e6a23c;
  margin-left: 4px;
}
</style>
